import { CookedCategory, ModelAnimation } from '@/types/dmaker/category.type'
import { Resource } from '@/types/dmaker/resource.type'
import { createFeModelId } from '@/store/creative/common/category'
import useIdToFindCategory from '@/views/creative/utils/use-id-to-find-category'
import _ from 'lodash'
import {
  isCocosModelAnimationResource,
  isCocosModelResource,
} from '@/views/creative/utils/index'

export function getAnimationList(category: CookedCategory): ModelAnimation[] {
  return _.get(category, 'properties.model_animation_list', []) || []
}

export function getAnimationBindModelIds(category: CookedCategory): number[] {
  return _.get(category, 'properties.animation_bind_model_ids', []) || []
}

export function getAllModelResource(resource: {
  [key: number]: Resource[]
}): Resource[] {
  const resList: Resource[] = []
  if (!_.isEmpty(resource)) {
    _.each(resource, (item) => {
      if (item.some((res) => isCocosModelResource(res))) {
        resList.push(...item)
      }
    })
  }
  return resList
}

export function getAllAnimationResources(resource: {
  [key: number]: Resource[]
}): Resource[] {
  const resList: Resource[] = []
  if (!_.isEmpty(resource)) {
    _.each(resource, (item) => {
      if (item.some((res) => isCocosModelAnimationResource(res))) {
        resList.push(...item)
      }
    })
  }
  return resList
}

/**
 * 填充当前模型坑位的默认动作数据（根据命名来匹配）
 * @param modelAnimationResource 所有的动画资源
 * @param category 当前模型分类
 */
export function assignDefaultAnimationToProperties({
  modelAnimationResource,
  category,
}: {
  modelAnimationResource: Resource[]
  category: CookedCategory
}): void {
  if (
    category.properties &&
    category.properties.model_animation_list &&
    category.properties.model_animation_list.length
  ) {
    console.log('当前的模型坑位已经有动作数据，不需要填充')
    return
  }
  category.properties.model_animation_list = getDefaultAnimationList({
    modelAnimationResource,
    category,
  })
}

export function getDefaultAnimationList({
  modelAnimationResource,
  category,
}: {
  modelAnimationResource: Resource[]
  category: CookedCategory
}): ModelAnimation[] {
  const prefix = category.prefix
  const specialPrefix = `${prefix}@`
  return modelAnimationResource
    .filter((item) => item.prefix.indexOf(specialPrefix) === 0)
    .map((item) => {
      return {
        is_default: true,
        id: createFeModelId(),
        category_id: item.category_id,
        model_category_ids: [category.id],
        file_path: item.real_file_path,
        rotate: 0,
      }
    }) as ModelAnimation[]
}

/**
 * 替动作分类增加默认的模型数据（根据命名来匹配）
 * @param modelResource 所有的模型资源
 * @param category 动作分类
 */
export function assignDefaultModelToProperties({
  modelResource,
  category,
}: {
  modelResource: Resource[]
  category: CookedCategory
}): void {
  if (
    category.properties &&
    category.properties.animation_bind_model_ids &&
    category.properties.animation_bind_model_ids.length
  ) {
    console.log('当前的动画坑位已经有模型数据，不需要填充')
    return
  }
  const modelPrefix = category.prefix.split('@')[0]
  const modelRes = modelResource.find((item) => item.prefix === modelPrefix)
  if (modelRes) {
    category.properties.animation_bind_model_ids = [modelRes.category_id]
  }
}

/**
 * 更新模型坑位下面都动画列表数据，由于 id 也有可能被更新，所以统一通过 file_path 来做唯一标识
 * @param category
 * @param modelAnimation
 */
export function updateAnimationListByConfig({
  modelAnimationList,
  modelAnimation,
}: {
  modelAnimationList: ModelAnimation[]
  modelAnimation: ModelAnimation
}): ModelAnimation[] {
  const tempList = _.cloneDeep(modelAnimationList)
  if (tempList.find((item) => item.file_path === modelAnimation.file_path)) {
    const index = tempList.findIndex(
      (item) => item.file_path === modelAnimation.file_path,
    )
    const target = tempList[index]

    Object.assign(target, modelAnimation)
  } else {
    tempList.push(modelAnimation)
  }
  return tempList
}

/**
 * 通过动画分类获取动画数据，由于涉及绑定模型id, 所以需要传入模型的分类
 * @param modelAnimationResource
 * @param modelCategory
 * @param animationCategory
 */
export function getModelAnimationByAnimationCategory({
  modelAnimationResource,
  modelCategory,
  animationCategory,
}: {
  modelAnimationResource: Resource[]
  modelCategory: CookedCategory
  animationCategory: CookedCategory
}): ModelAnimation {
  const targetAnimationResource = modelAnimationResource.find(
    (item) => item.category_id === animationCategory.id,
  )
  const modelCategoryIds: number[] = []
  if (getAnimationBindModelIds(animationCategory)) {
    modelCategoryIds.push(...getAnimationBindModelIds(animationCategory))
  }
  if (modelCategoryIds.indexOf(modelCategory.id) === -1) {
    modelCategoryIds.push(modelCategory.id)
  }
  return {
    is_default: false,
    id: createFeModelId(),
    category_id: animationCategory.id,
    model_category_ids: modelCategoryIds,
    file_path: targetAnimationResource.real_file_path,
    rotate: 0,
  }
}

/**
 * 同步模型分类的动作数据到模板分类，需要做的操作如下
 * 1. 赋值模型分类最新的动作数据
 * 2. 根据最新的动作数据，绑定动作分类的 animation_bind_model_ids
 * 3. 根据最新的动作数据，删除其他模型分类的动作数据
 * @param modelAnimationList
 * @param modelCategory
 * @param templateCategory
 */
export function syncModelAnimationListToTemplateCategory({
  modelAnimationList,
  modelCategory,
  templateCategory,
}: {
  modelAnimationList: ModelAnimation[]
  modelCategory: CookedCategory
  templateCategory: CookedCategory[]
}): void {
  const oldModelCategoryList = getAnimationList(modelCategory)
  _.set(
    modelCategory,
    'properties.model_animation_list',
    _.cloneDeep(modelAnimationList),
  )
  oldModelCategoryList.forEach((item) => {
    const index = modelAnimationList.findIndex(
      (i) => i.category_id === item.category_id,
    )
    // 如果历史数据有，但是新的数据没有，则代表是删除的动作数据，需要同时更新 animation_bind_model_ids
    if (index === -1) {
      const targetCategory = useIdToFindCategory({
        category: templateCategory,
        categoryId: item.category_id,
      })
      if (targetCategory) {
        const modelIds = getAnimationBindModelIds(targetCategory)
        const modelIndex = modelIds.indexOf(modelCategory.id)
        if (modelIndex !== -1) {
          modelIds.splice(modelIndex, 1)
          targetCategory.properties.animation_bind_model_ids = modelIds
        }
      }
    }
  })
  modelAnimationList.forEach((item) => {
    const targetCategory = useIdToFindCategory({
      category: templateCategory,
      categoryId: item.category_id,
    })
    if (targetCategory) {
      const modelIds = getAnimationBindModelIds(targetCategory)
      // 如果当前动画分类里面的模型id不存在，则添加
      if (!modelIds.includes(modelCategory.id)) {
        modelIds.push(modelCategory.id)
        targetCategory.properties.animation_bind_model_ids = modelIds
        // 涉及到新增之后，需要反推出 model 的坑位，把模型坑位中的 model_animation_list 也联动更新
        modelIds.forEach((modelCategoryId) => {
          const modelCategory = useIdToFindCategory({
            category: templateCategory,
            categoryId: modelCategoryId,
          })
          if (modelCategory) {
            const target = getAnimationList(modelCategory).find((animation) => {
              return animation.category_id === targetCategory.id
            })
            target.model_category_ids = modelIds
          }
        })
      }
    }
  })
}

/**
 * 通过动画分类以及路径获取动画旋转角度源数据
 * @param category 分配
 * @param path 路径
 */
export function getAnimationOriginRotateByPath(
  category: CookedCategory,
  path: ModelAnimation['file_path'],
): ModelAnimation['rotate'] {
  const animation = getAnimationList(category).find(
    (item) => item.file_path === path,
  )
  return animation?.rotate || 0
}

export function getAnimationRotateDiff(
  oldRotate: number,
  newRotate: number,
): ModelAnimation['rotate'] {
  return newRotate - oldRotate < 0
    ? 360 + (newRotate - oldRotate)
    : newRotate - oldRotate
}
